import { Backbone } from "extensions/backbone";
import { isObject } from "lodash-es";

const dateList = ["settle_date", "activity_date"];
const blacklist = new Set(["ccnum", "purchase_amount_type", "stl_amount_type", "amount_type"]);
const currencyFields = [
    { field: "purchase_currency", amount: "purchase_amount" },
    { field: "stl_currency", amount: "stl_amount" },
    { field: "currency", amount: "amount" }
];
const primaryFields = {
    sales_refunds: [
        "pz_transaction_ref",
        ["iin", "last_four"],
        "external_ref_id",
        "purchase_amount",
        ["activity_date_start", "activity_date_end"]
    ],
    chargebacks: [
        "pz_transaction_ref",
        ["iin", "last_four"],
        "dispute_id",
        "amount",
        ["activity_date_start", "activity_date_end"],
        "original_order_or_transaction_id"
    ],
    authorizations: [
        "pz_transaction_ref",
        ["iin", "last_four"],
        "external_ref_id",
        "auth_code",
        "amount",
        ["activity_date_start", "activity_date_end"]
    ]
};

export const SearchFormModel = Backbone.Model.extend({
    defaults: {
        iin: "",
        last_four: ""
    },
    
    initialize(props = {}, options = {}) {
        if (Object.prototype.hasOwnProperty.call(options, "section")) {
            this.section = options.section;
        }
    },
    
    set(key, value, options) {
        if (!isObject(key) && dateList.includes(key)) {
            return this.setDate(key, value);
        }
        return Backbone.Model.prototype.set.call(this, key, value, options);
    },
    
    setDate(key, value) {
        const dates = value.split("_");
        const data = {};
        data[`${key}_start`] = dates[0];
        data[`${key}_end`] = dates[1];
        this.set(data);
    },
    
    toJSON() {
        const json = Backbone.Model.prototype.toJSON.call(this);
        dateList.forEach((prop) => delete json.prop);
        Object.keys(json).forEach((key) => {
            if (json[key] === "" || blacklist.has(key)) {
                delete json[key];
            }
            // strip out currency fields that don't have corresponding amount field
            const currencyField = currencyFields.find((f) => f.field === key);
            if (currencyField && !json[currencyField.amount]) {
                delete json[key];
            }
        });
        return json;
    },
    
    validate(attrs) {
        const fieldsToIgnore = {};
        
        currencyFields.forEach((c) => {
            // If the model has a currency field, but doesn't have amount then don't add it in model length validation
            if (Object.prototype.hasOwnProperty.call(attrs, c.field) && !Object.prototype.hasOwnProperty.call(attrs, c.amount)) {
                fieldsToIgnore[c.field] = attrs[c.field];
            }
        });
        
        const errors = [];

        const mongo_ref_pattern = /^[\d,a-f]{24}$/;
        if (attrs.pz_transaction_ref && !mongo_ref_pattern.test(attrs.pz_transaction_ref)) {
            errors.push("Please make sure the Pazien transaction reference consists of 24 alphanumeric characters (0-9, lowercase a-f)");
        }

        const populatedFields = Object.entries(attrs).filter(([key, value]) => !Object.prototype.hasOwnProperty.call(fieldsToIgnore, key) && Boolean(value));
        if (populatedFields.length === 0) {
            errors.push("All primary search fields are empty.");
        }
        
        if (((attrs.iin !== "") || (attrs.last_four !== "")) && !((attrs.iin.length === 6 || attrs.iin.length === 8) && (attrs.last_four.length === 2 || attrs.last_four.length === 4))) {
            errors.push("Card number must be entered as either first 6 AND last 4 or first 8 AND last 2 or 4 digits.");
        }
        
        // At least one primary field (or a group) has to be selected
        const fields = primaryFields[this.section] || [];
        const selectedPrimaryFields = fields.reduce((memo, acc) => {
            const _acc = Array.isArray(acc) ? acc : [acc];
            if (_acc.every((f) => populatedFields.find(([key]) => key === f))) {
                memo = memo + 1;
            }
            return memo;
        }, 0);
        
        if (fields.length && selectedPrimaryFields === 0) {
            errors.push("Please add information to at least one primary search field.");
        }
        
        if (errors.length) {
            return errors;
        }
    }
});
